/**
* Utility functions for safe base64 encoding that handles Unicode characters
*/
 
/**
* Normalizes smart quotes and special characters to ASCII equivalents
* @param {string} str - The string to normalize
* @returns {string} - The normalized string
*/
export const normalizeQuotes = (str) => {
  if (typeof str !== 'string') return str;
  
  return str
    // Smart double quotes to standard double quotes
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
    // Smart single quotes to standard single quotes
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
    // Em dash and en dash to standard dash
    .replace(/[\u2013\u2014]/g, '-')
    // Ellipsis
    .replace(/\u2026/g, '...')
    // Non-breaking space
    .replace(/\u00A0/g, ' ')
    // Other common problematic characters
    .replace(/\u2022/g, '*') // Bullet point
    .replace(/\u00AB/g, '<<') // Left guillemet
    .replace(/\u00BB/g, '>>') // Right guillemet
    // Arrow symbols
    .replace(/[\u2190\u2191\u2192\u2193]/g, '->') // Left, Up, Right, Down arrows
    .replace(/[\u21D0\u21D1\u21D2\u21D3]/g, '=>') // Double arrows
    // Mathematical symbols
    .replace(/\u00D7/g, 'x') // Multiplication sign
    .replace(/\u00F7/g, '/') // Division sign
    .replace(/\u2248/g, '~') // Approximately equal
    .replace(/\u2264/g, '<=') // Less than or equal
    .replace(/\u2265/g, '>=') // Greater than or equal
    .replace(/\u2260/g, '!=') // Not equal

    .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')



    // Warning and checkmark symbols
    // .replace(/\u26A0/g, '[WARNING]') // Warning sign
    // .replace(/\u2705/g, '[CHECK]') // Check mark
    // .replace(/\u2713/g, '[CHECK]') // Check mark variant
    // .replace(/\u2714/g, '[CHECK]') // Heavy check mark
    // .replace(/\u274C/g, '[X]') // Cross mark
    // // Common emojis that cause encoding issues - use a more efficient approach
    // .replace(/[\u{1F000}-\u{1F9FF}]/gu, '[EMOJI]') // Emoji range 1
    // .replace(/[\u{2600}-\u{26FF}]/gu, '[SYMBOL]') // Miscellaneous symbols
    // .replace(/[\u{2700}-\u{27BF}]/gu, '[SYMBOL]') // Dingbats
    // .replace(/[\u{1F680}-\u{1F6FF}]/gu, '[EMOJI]') // Transport and map symbols
    // .replace(/[\u{1F300}-\u{1F5FF}]/gu, '[EMOJI]') // Misc symbols and pictographs
    // .replace(/[\u{1F600}-\u{1F64F}]/gu, '[EMOJI]') // Emoticons
    // .replace(/[\u{1F650}-\u{1F67F}]/gu, '[EMOJI]') // Ornamental dingbats
    // .replace(/[\u{1F700}-\u{1F77F}]/gu, '[EMOJI]') // Alchemical symbols
    // .replace(/[\u{1F780}-\u{1F7FF}]/gu, '[EMOJI]') // Geometric shapes extended
    // .replace(/[\u{1F800}-\u{1F8FF}]/gu, '[EMOJI]') // Supplemental arrows-C
    // .replace(/[\u{1F900}-\u{1F9FF}]/gu, '[EMOJI]') // Supplemental symbols and pictographs
    // .replace(/[\u{1FA00}-\u{1FA6F}]/gu, '[EMOJI]') // Chess symbols
    // .replace(/[\u{1FA70}-\u{1FAFF}]/gu, '[EMOJI]') // Symbols and pictographs extended-A
    // .replace(/[\u{1FB00}-\u{1FBFF}]/gu, '[EMOJI]') // Symbols for legacy computing
    // // Currency symbols
    // .replace(/[\u20A0-\u20CF]/g, '[CURRENCY]') // Currency symbols
    // .replace(/[\uFE00-\uFE0F]/g, '') // Variation selectors
    // .replace(/[\uFE20-\uFE2F]/g, '') // Combining half marks
    // Control characters
    // eslint-disable-next-line no-control-regex
    // .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
    // .replace(/[\u200B-\u200D\uFEFF]/g, '');// Zero-width space, joiner, non-joiner, BOM
    // Additional problematic Unicode ranges
    // .replace(/[\u2B00-\u2BFF]/g, '[SYMBOL]') // Miscellaneous Symbols and Arrows
    // .replace(/[\u3000-\u303F]/g, '[SYMBOL]') // CJK Symbols and Punctuation
    // .replace(/[\uFF00-\uFFEF]/g, '[SYMBOL]'); // Halfwidth and Fullwidth Forms
};
 
/**
* Recursively normalizes quotes in objects and arrays
* @param {any} obj - The object, array, or primitive to normalize
* @returns {any} - The normalized value
*/
export const normalizeObjectQuotes = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'string') {
    return normalizeQuotes(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => normalizeObjectQuotes(item));
  }
  
  if (typeof obj === 'object') {
    const normalized = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        normalized[key] = normalizeObjectQuotes(obj[key]);
      }
    }
    return normalized;
  }
  
  return obj;
};
 
/**
* Safely encodes a string to base64, handling Unicode characters
* @param {string} str - The string to encode
* @returns {string} - The base64 encoded string
*/
/**
* Fully Unicode-safe Base64 utilities using TextEncoder/TextDecoder
*/
 
export const safeBtoa = (str) => {
  try {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  } catch (error) {
    console.error('Error encoding to base64:', error);
    throw new Error('Failed to encode string to base64: ' + error.message);
  }
};
 
export const safeAtob = (base64) => {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array([...binary].map((c) => c.charCodeAt(0)));
    return new TextDecoder('utf-8').decode(bytes);
  } catch (error) {
    console.error('Error decoding base64:', error);
    throw new Error('Failed to decode base64 string: ' + error.message);
  }
};
 
export const safeJsonBtoa = (obj, normalizeQuotesFirst = true) => {
  try {
    const processed = normalizeQuotesFirst ? normalizeObjectQuotes(obj) : obj;
    const jsonString = JSON.stringify(processed);
    return safeBtoa(jsonString);
  } catch (error) {
    console.error('Error encoding object to base64:', error);
    throw new Error('Failed to encode object to base64: ' + error.message);
  }
};
 
export const safeJsonAtob = (base64) => {
  try {
    const jsonString = safeAtob(base64);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error decoding base64 JSON:', error);
    throw new Error('Failed to decode base64 string to object: ' + error.message);
  }
};

/**
 * Intelligently decodes base64 data, trying safe method first, then fallback to unsafe method
 * This handles both new safe-encoded data and legacy unsafe-encoded data
 * @param {string} base64 - The base64 encoded data
 * @returns {any} - The decoded object
 */
export const smartJsonAtob = (base64) => {
  try {
    // First try the safe method (for new data)
    return safeJsonAtob(base64);
  } catch (error) {
    try {
      // Fallback to unsafe method (for legacy data)
      const jsonString = atob(base64);
      return JSON.parse(jsonString);
    } catch (fallbackError) {
      console.error('Both safe and unsafe decoding failed:', error, fallbackError);
      throw new Error('Failed to decode base64 string with both methods: ' + fallbackError.message);
    }
  }
};

/**
 * Recursively fixes emoji corruption in objects by detecting and correcting corrupted Unicode sequences
 * @param {any} obj - The object to fix
 * @returns {any} - The object with fixed emojis
 */
// export const fixEmojiCorruption = (obj) => {
//   if (obj === null || obj === undefined) {
//     return obj;
//   }
  
//   if (typeof obj === 'string') {
//     return fixEmojiInString(obj);
//   }
  
//   if (Array.isArray(obj)) {
//     return obj.map(item => fixEmojiCorruption(item));
//   }
  
//   if (typeof obj === 'object') {
//     const fixed = {};
//     for (const key in obj) {
//       if (obj.hasOwnProperty(key)) {
//         fixed[key] = fixEmojiCorruption(obj[key]);
//       }
//     }
//     return fixed;
//   }
  
//   return obj;
// };

// /**
//  * Fixes emoji corruption in a string by detecting common corruption patterns
//  * @param {string} str - The string to fix
//  * @returns {string} - The string with fixed emojis
//  */
// const fixEmojiInString = (str) => {
//   if (typeof str !== 'string') return str;
  
//   let fixedStr = str;
  
//   // Fix specific emoji corruption patterns with context
//   // Puzzle piece emoji
//   fixedStr = fixedStr.replace(/ð§©/g, '🧩');
  
//   // Movie camera emoji
//   fixedStr = fixedStr.replace(/ð¬/g, '🎬');
  
//   // Pencil emoji
//   fixedStr = fixedStr.replace(/âï¸/g, '✏️');
  
//   // Lungs emoji
//   fixedStr = fixedStr.replace(/ð«/g, '🫁');
  
//   // Checkmark emoji - look for context clues like "good response", "success", etc.
//   fixedStr = fixedStr.replace(/â(?=.*(?:good response|success|indicator|completed|done|yes))/gi, '✅');
  
//   // Warning emoji - look for context clues like "mild frown", "warning", "caution", etc.
//   fixedStr = fixedStr.replace(/â(?=.*(?:mild frown|warning|caution|attention|alert))/gi, '⚠️');
  
//   // Default checkmark for remaining â characters (most common case)
//   fixedStr = fixedStr.replace(/â/g, '✅');
  
//   return fixedStr;
// };


/**
 * Fixes emoji corruption in objects recursively
 * @param {any} obj - The object to fix
 * @returns {any} - The object with fixed emojis
 */
export const fixEmojiCorruption = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'string') {
    return fixEmojiInString(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => fixEmojiCorruption(item));
  }
  
  if (typeof obj === 'object') {
    const fixed = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        fixed[key] = fixEmojiCorruption(obj[key]);
      }
    }
    return fixed;
  }
  
  return obj;
};

/**
 * Fixes emoji corruption in a string by decoding double-encoded UTF-8
 * @param {string} str - The string to fix
 * @returns {string} - The string with fixed emojis
 */
const fixEmojiInString = (str) => {
  if (typeof str !== 'string') return str;
  
  try {
    // Convert the corrupted string back to bytes
    const bytes = [];
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      // Handle both single-byte and multi-byte characters
      if (code <= 0xFF) {
        bytes.push(code);
      } else {
        // For characters outside Latin-1 range, encode them as UTF-8
        const encoded = encodeURIComponent(str[i]);
        for (let j = 0; j < encoded.length; j += 3) {
          if (encoded[j] === '%') {
            bytes.push(parseInt(encoded.substr(j + 1, 2), 16));
          }
        }
      }
    }
    
    // Decode the bytes as UTF-8
    const uint8Array = new Uint8Array(bytes);
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const decoded = decoder.decode(uint8Array);
    
    // If decoding improved the string (has actual emojis), return it
    // Otherwise return the original
    if (hasEmojis(decoded) || decoded !== str) {
      return decoded;
    }
    
    return str;
  } catch (e) {
    // If decoding fails, return original string
    console.warn('Failed to decode emoji corruption:', e);
    return str;
  }
};

/**
 * Check if a string contains emoji characters
 * @param {string} str - The string to check
 * @returns {boolean} - True if string contains emojis
 */
const hasEmojis = (str) => {
  // Emoji regex pattern that matches most emoji ranges
  const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2300}-\u{23FF}\u{2B50}\u{2934}-\u{2935}\u{3030}\u{3297}\u{3299}\u{FE0F}\u{203C}\u{2049}\u{20E3}\u{2122}\u{2139}\u{2194}-\u{2199}\u{21A9}-\u{21AA}\u{231A}-\u{231B}\u{2328}\u{23CF}\u{23E9}-\u{23F3}\u{23F8}-\u{23FA}\u{24C2}\u{25AA}-\u{25AB}\u{25B6}\u{25C0}\u{25FB}-\u{25FE}\u{2600}-\u{2604}\u{260E}\u{2611}\u{2614}-\u{2615}\u{2618}\u{261D}\u{2620}\u{2622}-\u{2623}\u{2626}\u{262A}\u{262E}-\u{262F}\u{2638}-\u{263A}\u{2640}\u{2642}\u{2648}-\u{2653}\u{265F}-\u{2660}\u{2663}\u{2665}-\u{2666}\u{2668}\u{267B}\u{267E}-\u{267F}\u{2692}-\u{2697}\u{2699}\u{269B}-\u{269C}\u{26A0}-\u{26A1}\u{26A7}\u{26AA}-\u{26AB}\u{26B0}-\u{26B1}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26C8}\u{26CE}-\u{26CF}\u{26D1}\u{26D3}-\u{26D4}\u{26E9}-\u{26EA}\u{26F0}-\u{26F5}\u{26F7}-\u{26FA}\u{26FD}\u{2702}\u{2705}\u{2708}-\u{270D}\u{270F}\u{2712}\u{2714}\u{2716}\u{271D}\u{2721}\u{2728}\u{2733}-\u{2734}\u{2744}\u{2747}\u{274C}\u{274E}\u{2753}-\u{2755}\u{2757}\u{2763}-\u{2764}\u{2795}-\u{2797}\u{27A1}\u{27B0}\u{27BF}\u{2934}-\u{2935}\u{2B05}-\u{2B07}\u{2B1B}-\u{2B1C}\u{2B50}\u{2B55}\u{3030}\u{303D}\u{3297}\u{3299}]/u;
  return emojiRegex.test(str);
};

// Example usage and test cases
if (typeof window !== 'undefined') {
  // Browser environment - add to window for testing
  window.fixEmojiCorruption = fixEmojiCorruption;
}
 
 